import React, { PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
    ProgressIndicator,
    SHAPE_CIRCULAR,
    SIZE_SMALL_THIN,
    STYLE_GRADIENT,
} from '@eventbrite/legacy-eds.progress-indicator';
import { IMAGE_LOADER_STATUSES, VERTICAL_ALIGNMENTS_PROP_TYPE } from './constants';
import styles from './ListItem.module.scss';

const ExtraContent = ({ children }) => {
    let component = null;

    if (children) {
        component = (
            <div
                className={classNames(styles.aside, styles.extraContent)}
                data-testid="list-item-extra-content"
            >
                {children}
            </div>
        );
    }

    return component;
};

class ItemImageComponent extends PureComponent {
    static propTypes = {
        /**
         * Contents of icon list item
         */
        children: PropTypes.node.isRequired,
        /**
         * Generic content for the left side
         */
        extraContent: PropTypes.node,
        /**
         * Vertical alignment of children items
         */
        verticalAlignment: VERTICAL_ALIGNMENTS_PROP_TYPE,
        /**
         * URL of image to be displayed with list item content
         */
        imageUrl: PropTypes.string,
        /**
         * Alt text for image to be displayed with list item content
         */
        imageAlt: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                defaultMessage: PropTypes.string.isRequired,
            }),
        ]),
        /**
         * Optional square shape image - note you must pass in an imageUrl
         * for an image that is either 1:1 or wider than it is tall.
         */
        isSquareImage: PropTypes.bool,
        /**
         * Whether or not the item should use a progress indicator while the image is loading
         */
        progressIndicator: PropTypes.bool,
    };

    static defaultProps = {
        verticalAlignment: 'Middle',
        progressIndicator: false,
        isSquareImage: false,
    };

    state = {
        imageStatus: IMAGE_LOADER_STATUSES.LOADING_STATE,
    };

    _handleImageLoaded = () => this.setState({ imageStatus: IMAGE_LOADER_STATUSES.LOADED_STATE });

    _handleImageError = () => this.setState({ imageStatus: IMAGE_LOADER_STATUSES.FAILED_STATE });

    render() {
        const {
            intl,
            children,
            extraContent,
            imageAlt,
            imageUrl,
            isSquareImage,
            progressIndicator,
            verticalAlignment,
        } = this.props;

        let itemImageComponent;
        let progressIndicatorComponent = null;
        const { imageStatus } = this.state;

        const className = classNames(styles.root, {
            [styles[`align-${verticalAlignment}`]]: verticalAlignment,
        });

        const classNameGraphic = classNames(styles.aside, styles.graphic, {
            [styles.graphicSquare]: isSquareImage,
        });

        const classNameImage = classNames({
            [styles.show]:
                imageStatus === IMAGE_LOADER_STATUSES.LOADED_STATE ||
                imageStatus === IMAGE_LOADER_STATUSES.FAILED_STATE,
            [styles.hide]: imageStatus === IMAGE_LOADER_STATUSES.LOADING_STATE,
        });

        const imageAltValue =
            typeof imageAlt === 'object' && imageAlt !== null
                ? intl.formatMessage(imageAlt)
                : imageAlt;

        if (progressIndicator) {
            /**
             * In the case of  the failed load, the progress indicator should be unrendered
             * and the img tag should should the alt image.
             */
            if (imageStatus === IMAGE_LOADER_STATUSES.LOADING_STATE) {
                progressIndicatorComponent = (
                    <div className={styles.graphicIndicator}>
                        <ProgressIndicator
                            shape={SHAPE_CIRCULAR}
                            size={SIZE_SMALL_THIN}
                            style={STYLE_GRADIENT}
                        />
                    </div>
                );
            }

            itemImageComponent = (
                <img
                    src={imageUrl}
                    alt={imageAltValue}
                    className={classNameImage}
                    onLoad={this._handleImageLoaded}
                    onError={this._handleImageError}
                    data-testid="list-item-graphic-image--progress-indicator-img"
                />
            );
        } else {
            itemImageComponent = (
                <img src={imageUrl} alt={imageAltValue} data-testid="list-item-graphic-image" />
            );
        }

        return (
            <div className={className} data-testid="eds-list-item">
                <ExtraContent>{extraContent}</ExtraContent>
                <span
                    className={classNameGraphic}
                    data-testid="list-item-graphic"
                    alt={imageAltValue}
                >
                    {progressIndicatorComponent}
                    {itemImageComponent}
                </span>
                <div className={styles.contents} data-testid="eds-list-item-contents">
                    {children}
                </div>
            </div>
        );
    }
}

export const ItemImage = injectIntl(ItemImageComponent);
